import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { useTracking } from "@/context/TrackingContext";
import { toast } from "sonner";
import { Clock, CheckCircle, CreditCard, Star, Zap, Globe, Volume2, VolumeX, Loader2, AlertCircle, Calendar as CalendarIcon, Play, Pause } from "lucide-react";
import ListenerFaqModule from "@/components/ListenerFaqModule";
import { VideoSubtitles } from "@/components/VideoSubtitles";
import { ChatbotWidget } from "@/components/ChatbotWidget";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LEGAL_DISCLAIMER = "Este servicio es de acompañamiento y escucha. No es un servicio médico y no sustituye atención profesional.";

const DEFAULT_PHOTO = "https://images.unsplash.com/photo-1629121699638-976764fb4c8c?w=600&h=800&fit=crop";

const getFullUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export default function ListenerProfile() {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { trackViewProfile, trackVideoPlay, trackClickCTA, trackStartCheckout } = useTracking();
  const videoRef = useRef(null);
  
  // Refs for auto-scroll
  const calendarRef = useRef(null);
  const slotsRef = useRef(null);
  const summaryRef = useRef(null);
  
  const [listener, setListener] = useState(null);
  const [loading, setLoading] = useState(true);
  const [headline, setHeadline] = useState("Habla con alguien que te escuche hoy mismo");
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [slotsError, setSlotsError] = useState(null);
  const [showBookingDialog, setShowBookingDialog] = useState(false);
  const [showInstantDialog, setShowInstantDialog] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [instantConsent, setInstantConsent] = useState(false);
  const [booking, setBooking] = useState(false);
  // Video state - single source of truth
  const [isMuted, setIsMuted] = useState(true); // iOS requires muted for autoplay
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);
  const [showVideo, setShowVideo] = useState(false); // Only show when buffered
  const [subtitles, setSubtitles] = useState(null); // Video subtitles
  
  // Currency selection state
  const [pricingInfo, setPricingInfo] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState("usd");
  const [loadingPricing, setLoadingPricing] = useState(false);

  useEffect(() => {
    fetchListener();
    
    const params = new URLSearchParams(window.location.search);
    if (params.get('cancelled') === 'true') {
      const errorType = params.get('error');
      if (errorType === 'card_declined') {
        toast.error("Tu tarjeta fue rechazada. Si es una tarjeta mexicana, puede que no soporte pagos en USD. Intenta con otra tarjeta.", {
          duration: 8000
        });
      } else {
        toast.error("Pago cancelado. Puedes intentar de nuevo cuando quieras.");
      }
    }
  }, [username]);

  useEffect(() => {
    if (selectedDate && listener) {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      fetchSlots(dateStr);
    }
  }, [selectedDate, listener]);

  const fetchListener = async () => {
    try {
      const response = await axios.get(`${API}/listeners/${username}`);
      setListener(response.data);
      
      // Track view profile event
      trackViewProfile(response.data.id, response.data.name);
      
      // Fetch headlines
      fetchHeadlines(response.data.id);
      
      // Fetch pricing info with currency detection
      fetchPricingInfo(response.data.id);
      
      // Fetch video subtitles
      fetchSubtitles(response.data.id);
    } catch (error) {
      console.error("Error fetching listener:", error);
      toast.error("Listener no encontrado");
      navigate("/explorar");
    } finally {
      setLoading(false);
    }
  };

  const fetchHeadlines = async (listenerId) => {
    try {
      const response = await axios.get(`${API}/config/headlines?listener_id=${listenerId}`);
      if (response.data.headlines && response.data.headlines.length > 0) {
        // Pick random headline
        const randomIndex = Math.floor(Math.random() * response.data.headlines.length);
        setHeadline(response.data.headlines[randomIndex]);
      }
    } catch (error) {
      console.error("Error fetching headlines:", error);
    }
  };

  const fetchPricingInfo = async (listenerId) => {
    setLoadingPricing(true);
    try {
      const response = await axios.get(`${API}/config/pricing-info/${listenerId}`);
      setPricingInfo(response.data);
      // Set suggested currency as default
      setSelectedCurrency(response.data.suggested_currency || "usd");
    } catch (error) {
      console.error("Error fetching pricing info:", error);
      // Fallback to USD if pricing info fails
      setSelectedCurrency("usd");
    } finally {
      setLoadingPricing(false);
    }
  };

  // Fetch video subtitles
  const fetchSubtitles = async (listenerId) => {
    try {
      const response = await axios.get(`${API}/listeners/${listenerId}/subtitles`);
      if (response.data.subtitles) {
        setSubtitles(response.data.subtitles);
      }
    } catch (error) {
      console.error("Error fetching subtitles:", error);
    }
  };

  // Get display price based on selected currency
  const getDisplayPrice = () => {
    if (!pricingInfo) {
      return { amount: listener?.price_per_session || 45, currency: "USD" };
    }
    
    if (selectedCurrency === "mxn") {
      return { amount: pricingInfo.price_mxn, currency: "MXN" };
    }
    return { amount: pricingInfo.price_usd, currency: "USD" };
  };

  const fetchSlots = async (date) => {
    if (!listener) return;
    setLoadingSlots(true);
    setSlotsError(null);
    setSelectedSlot(null);
    
    try {
      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await axios.get(`${API}/listeners/${listener.id}/slots?date=${date}&user_timezone=${userTimezone}`);
      
      if (response.data.slots && response.data.slots.length > 0) {
        setAvailableSlots(response.data.slots);
      } else {
        setAvailableSlots([]);
        setSlotsError("No hay horarios disponibles para este día. Intenta con otra fecha.");
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      setAvailableSlots([]);
      setSlotsError("Error al cargar horarios. Intenta de nuevo.");
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleBooking = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para agendar");
      // Save return URL so user comes back after login
      const returnUrl = `/l/${username}`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    if (!consentAccepted) {
      toast.error("Debes aceptar el consentimiento");
      return;
    }

    setBooking(true);
    
    // Track checkout start
    const price = pricingInfo ? (selectedCurrency === "mxn" ? pricingInfo.price_mxn : pricingInfo.price_usd) : listener.price_per_session;
    trackStartCheckout(listener.id, price, selectedCurrency.toUpperCase());
    
    try {
      const response = await axios.post(`${API}/sessions/checkout`, {
        listener_id: listener.id,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: typeof selectedSlot === 'object' ? selectedSlot.time : selectedSlot,
        consent_accepted: consentAccepted,
        origin_url: window.location.origin,
        selected_currency: selectedCurrency
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No se recibió URL de pago");
      }
    } catch (error) {
      console.error("Error booking session:", error);
      const errorMsg = error.response?.data?.detail || "Error al procesar";
      // Check for currency-related errors
      if (errorMsg.toLowerCase().includes("currency") || errorMsg.toLowerCase().includes("moneda")) {
        toast.error("Tu tarjeta no soporta esta moneda. Intenta cambiar a " + (selectedCurrency === "mxn" ? "USD" : "MXN"), {
          duration: 6000
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setBooking(false);
    }
  };

  // Handle instant booking ("Hablar ahora")
  const handleInstantBooking = async () => {
    if (!user) {
      toast.error("Debes iniciar sesión para hablar ahora");
      // Save return URL so user comes back after login
      const returnUrl = `/l/${username}`;
      navigate(`/login?redirect=${encodeURIComponent(returnUrl)}`);
      return;
    }

    // Open consent dialog for instant booking
    setShowInstantDialog(true);
  };

  const confirmInstantBooking = async () => {
    if (!instantConsent) {
      toast.error("Debes aceptar el consentimiento");
      return;
    }

    setBooking(true);
    try {
      const response = await axios.post(`${API}/sessions/instant-checkout`, {
        listener_id: listener.id,
        consent_accepted: instantConsent,
        origin_url: window.location.origin,
        selected_currency: selectedCurrency
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No se recibió URL de pago");
      }
    } catch (error) {
      console.error("Error instant booking:", error);
      const errorMsg = error.response?.data?.detail || "Error al procesar";
      if (errorMsg.toLowerCase().includes("currency") || errorMsg.toLowerCase().includes("moneda")) {
        toast.error("Tu tarjeta no soporta esta moneda. Intenta cambiar a " + (selectedCurrency === "mxn" ? "USD" : "MXN"), {
          duration: 6000
        });
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setBooking(false);
      setShowInstantDialog(false);
    }
  };

  const openBookingDialog = () => {
    setShowBookingDialog(true);
    setSelectedDate(null);
    setSelectedSlot(null);
    setAvailableSlots([]);
    setConsentAccepted(false);
    setSlotsError(null);
    // Small delay to ensure dialog is open, then scroll to calendar
    setTimeout(() => {
      calendarRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };
  
  // Auto-scroll when date is selected (scroll to slots)
  useEffect(() => {
    if (selectedDate && showBookingDialog) {
      setTimeout(() => {
        slotsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [selectedDate, showBookingDialog]);
  
  // Auto-scroll when slot is selected (scroll to summary/consent)
  useEffect(() => {
    if (selectedSlot && showBookingDialog) {
      setTimeout(() => {
        summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300);
    }
  }, [selectedSlot, showBookingDialog]);

  const toggleMute = () => {
    if (videoRef.current) {
      const newMuted = !isMuted;
      videoRef.current.muted = newMuted;
      setIsMuted(newMuted);
    }
  };

  // Toggle video play/pause
  const toggleVideoPlay = () => {
    if (videoRef.current) {
      if (videoRef.current.paused) {
        videoRef.current.play().then(() => {
          setIsVideoPlaying(true);
        }).catch(() => {});
      } else {
        videoRef.current.pause();
        setIsVideoPlaying(false);
      }
    }
  };

  // Handle video canplaythrough - enough buffer to play smoothly
  const handleVideoReady = () => {
    setVideoReady(true);
    setShowVideo(true);
    // Try autoplay
    if (videoRef.current) {
      videoRef.current.play().then(() => {
        setIsVideoPlaying(true);
      }).catch(() => {
        setIsVideoPlaying(false);
      });
    }
  };

  // Handle video error - show fallback
  const handleVideoError = () => {
    setVideoFailed(true);
    setShowVideo(false);
  };

  // Video timeout - if not ready in 3 seconds, show fallback
  useEffect(() => {
    if (listener?.video_url && !videoReady && !videoFailed) {
      const timeout = setTimeout(() => {
        if (!videoReady) {
          setVideoFailed(true);
        }
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [listener?.video_url, videoReady, videoFailed]);

  // Track video play state changes
  const handleVideoPlay = () => setIsVideoPlaying(true);
  const handleVideoPause = () => setIsVideoPlaying(false);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  if (!listener) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <p className="text-stone-600">Listener no encontrado</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-stone-50">
        {/* ========== MOBILE LAYOUT - VIDEO SEPARADO DEL CONTENIDO ========== */}
        <div className="lg:hidden">
          
          {/* SECCIÓN 1: VIDEO (No tapa nada - arriba solo) */}
          <div className="relative bg-stone-900">
            {/* Aspect ratio container for video - 9:16 pero limitado */}
            <div className="relative w-full" style={{ paddingBottom: '100%', maxHeight: '50vh' }}>
              
              {/* Foto de perfil como fallback/fondo */}
              <img
                src={getFullUrl(listener.photo_url) || DEFAULT_PHOTO}
                alt={listener.name}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${showVideo ? 'opacity-0' : 'opacity-100'}`}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = DEFAULT_PHOTO;
                }}
              />
              
              {/* VIDEO - Solo se muestra cuando está listo para reproducirse fluido */}
              {listener.video_url && !videoFailed && (
                <video
                  ref={videoRef}
                  src={getFullUrl(listener.video_url)}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${showVideo ? 'opacity-100' : 'opacity-0'}`}
                  loop
                  muted
                  playsInline
                  autoPlay
                  preload="auto"
                  onCanPlayThrough={handleVideoReady}
                  onPlay={handleVideoPlay}
                  onPause={handleVideoPause}
                  onError={handleVideoError}
                />
              )}
              
              {/* Tap to pause/play */}
              {listener.video_url && showVideo && (
                <button
                  onClick={toggleVideoPlay}
                  className="absolute inset-0 z-10"
                  aria-label={isVideoPlaying ? "Pausar video" : "Reproducir video"}
                  data-testid="video-play-toggle"
                >
                  {/* Solo mostrar icono de pausa breve al pausar */}
                  {!isVideoPlaying && videoReady && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <div className="w-16 h-16 bg-white/80 rounded-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-stone-900 ml-1" fill="currentColor" />
                      </div>
                    </div>
                  )}
                </button>
              )}
              
              {/* Mute/Unmute button */}
              {listener.video_url && showVideo && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                  className="absolute bottom-3 right-3 px-3 py-2 bg-black/60 backdrop-blur-sm rounded-full flex items-center gap-2 text-white text-xs font-medium z-20"
                  data-testid="video-mute-toggle"
                >
                  {isMuted ? (
                    <>
                      <VolumeX className="w-4 h-4" />
                      <span>Activar audio</span>
                    </>
                  ) : (
                    <>
                      <Volume2 className="w-4 h-4" />
                      <span>Silenciar</span>
                    </>
                  )}
                </button>
              )}
              
              {/* Video Subtitles - positioned above mute button, doesn't block anything */}
              {listener.video_url && showVideo && subtitles && (
                <VideoSubtitles 
                  videoRef={videoRef} 
                  subtitles={subtitles} 
                  isPlaying={isVideoPlaying}
                />
              )}
              
              {/* Badge disponibilidad */}
              {listener.is_online && listener.availability_mode === "instant" && (
                <div className="absolute top-3 left-3 bg-green-500 text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 shadow-lg z-20">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  Disponible ahora
                </div>
              )}
            </div>
          </div>
          
          {/* SECCIÓN 2: CONTENIDO (Debajo del video, NUNCA tapado) */}
          <div className="bg-white p-4 space-y-4">
            
            {/* Headline + Nombre */}
            <div>
              <p className="text-stone-600 text-base mb-1">{headline}</p>
              <h1 className="text-3xl font-serif font-bold text-stone-900" data-testid="listener-name">
                {listener.name}
              </h1>
            </div>
            
            {/* Rating & Sessions */}
            {(listener.average_rating || listener.total_sessions > 0) && (
              <div className="flex items-center gap-3 text-sm">
                {listener.average_rating && (
                  <div className="flex items-center gap-1 text-stone-700">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <span className="font-semibold">{listener.average_rating}</span>
                  </div>
                )}
                {listener.total_sessions > 0 && (
                  <span className="text-stone-500">
                    {listener.total_sessions}+ sesiones
                  </span>
                )}
              </div>
            )}
            
            {/* Precio + CTA "Agendar cita" */}
            <div className="flex items-center gap-4 pt-2">
              <div>
                <span className="text-3xl font-bold text-stone-900">
                  ${pricingInfo ? (selectedCurrency === "mxn" ? pricingInfo.price_mxn : pricingInfo.price_usd) : listener.price_per_session}
                </span>
                <span className="text-stone-500 text-sm ml-1 uppercase">{selectedCurrency}</span>
                <p className="text-xs text-stone-400">50 min de conversación</p>
              </div>
              <Button 
                className="flex-1 btn-primary py-4 text-base font-semibold shadow-lg"
                onClick={openBookingDialog}
                data-testid="book-session-btn-mobile"
              >
                Agendar cita
              </Button>
            </div>
            
            {/* Disclaimer legal - SIEMPRE VISIBLE */}
            <p className="text-xs text-stone-400 text-center pt-2 border-t border-stone-100">
              Servicio de acompañamiento y escucha • No es terapia
            </p>
          </div>
          
          {/* SECCIÓN 3: BLOQUE DE CONFIANZA */}
          <div className="p-4 space-y-4 bg-stone-50">
            {/* Social Proof */}
            {listener.total_sessions > 0 && (
              <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
                <p className="text-stone-700 text-sm text-center">
                  Más de <strong>{listener.total_sessions}</strong> conversaciones reales con personas que solo necesitaban hablar.
                </p>
                <div className="flex justify-center gap-4 mt-3 text-xs text-stone-500">
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5 text-primary" />
                    100% confidencial
                  </span>
                  <span className="flex items-center gap-1">
                    <CheckCircle className="w-3.5 h-3.5 text-primary" />
                    Sin compromiso
                  </span>
                </div>
              </div>
            )}

            {/* Bio */}
            {listener.bio && (
              <div className="bg-white rounded-xl p-4 border border-stone-100">
                <h2 className="font-semibold text-foreground mb-2">Sobre mí</h2>
                <p className="text-stone-600 text-sm leading-relaxed">{listener.bio}</p>
              </div>
            )}

            {/* Experience */}
            {listener.experience && (
              <div className="bg-white rounded-xl p-4">
                <h2 className="font-semibold text-foreground mb-2">Experiencia</h2>
                <p className="text-stone-600 text-sm leading-relaxed">{listener.experience}</p>
              </div>
            )}

            {/* Reviews */}
            {listener.recent_reviews && listener.recent_reviews.length > 0 && (
              <div className="bg-white rounded-xl p-4">
                <h2 className="font-semibold text-foreground mb-3">Reseñas</h2>
                <div className="space-y-3">
                  {listener.recent_reviews.slice(0, 3).map((review) => (
                    <div key={review.id} className="border-b border-stone-100 last:border-0 pb-3 last:pb-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`w-3 h-3 ${star <= review.rating ? "text-amber-400 fill-current" : "text-stone-300"}`}
                            />
                          ))}
                        </div>
                        <span className="text-xs text-stone-500">{review.user_name}</span>
                      </div>
                      {review.comment && <p className="text-sm text-stone-600">{review.comment}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* FAQ Module - High Converting */}
            <ListenerFaqModule
              listenerName={listener.name}
              onBookClick={openBookingDialog}
              onInstantClick={handleInstantBooking}
              showInstant={listener.is_online && listener.availability_mode === "instant"}
            />

            {/* Trust Block - What you get */}
            <div className="bg-primary/5 rounded-xl p-4 border border-primary/10">
              <h3 className="font-semibold text-foreground mb-3">Lo que incluye tu sesión</h3>
              <ul className="space-y-2 text-sm text-stone-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  50 minutos de conversación privada
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  Videollamada 1 a 1 con {listener.name.split(' ')[0]}
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  100% confidencial
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-primary flex-shrink-0" />
                  Escucha sin juicios
                </li>
              </ul>
            </div>

            {/* Legal Compliance Block */}
            <div className="bg-stone-100 rounded-xl p-4 text-xs text-stone-500 space-y-2">
              <p><strong>Aviso importante:</strong> {LEGAL_DISCLAIMER}</p>
              <p>Este es un servicio de escucha y conversación, no terapia. {listener.name.split(' ')[0]} no es profesional de salud mental.</p>
            </div>
          </div>
        </div>

        {/* Desktop Layout - Two columns */}
        <div className="hidden lg:block container-main py-8">
          <div className="grid grid-cols-3 gap-8">
            {/* Left - Video & Info */}
            <div className="col-span-2 space-y-6">
              {/* Video */}
              {listener.video_url && (
                <div className="relative aspect-video bg-stone-900 rounded-2xl overflow-hidden">
                  <video
                    ref={videoRef}
                    src={getFullUrl(listener.video_url)}
                    className="w-full h-full object-cover"
                    loop
                    muted={isMuted}
                    playsInline
                    autoPlay
                    preload="auto"
                  />
                  <button
                    onClick={toggleMute}
                    className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur rounded-full flex items-center justify-center text-white"
                  >
                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                  </button>
                </div>
              )}

              {/* Profile Header */}
              <div className="flex items-start gap-6">
                <img
                  src={getFullUrl(listener.photo_url) || DEFAULT_PHOTO}
                  alt={listener.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = DEFAULT_PHOTO;
                  }}
                />
                <div className="flex-1">
                  <div className="flex items-center gap-4">
                    <h1 className="text-3xl font-serif font-bold text-foreground">{listener.name}</h1>
                    {listener.is_online && listener.availability_mode === "instant" && (
                      <div className="flex items-center gap-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                        <Zap className="w-4 h-4" />
                        Disponible ahora
                      </div>
                    )}
                  </div>
                  
                  {listener.average_rating && (
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star key={star} className={`w-5 h-5 ${star <= Math.round(listener.average_rating) ? "text-amber-400 fill-current" : "text-stone-300"}`} />
                        ))}
                      </div>
                      <span className="font-semibold">{listener.average_rating}</span>
                      <span className="text-stone-500">({listener.total_reviews || 0} reseñas)</span>
                      {listener.total_sessions > 0 && (
                        <span className="text-stone-500">• {listener.total_sessions} sesiones</span>
                      )}
                    </div>
                  )}

                  <div className="flex items-center gap-4 mt-2 text-stone-600 text-sm">
                    <span className="flex items-center gap-1"><Globe className="w-4 h-4" />{listener.timezone}</span>
                    <span className="flex items-center gap-1"><Clock className="w-4 h-4" />50 min/sesión</span>
                  </div>
                </div>
              </div>

              {/* Bio & Experience */}
              {listener.bio && (
                <div className="bg-white rounded-2xl p-6">
                  <h2 className="font-semibold text-lg text-foreground mb-3">Sobre mí</h2>
                  <p className="text-stone-600 leading-relaxed">{listener.bio}</p>
                </div>
              )}

              {listener.experience && (
                <div className="bg-white rounded-2xl p-6">
                  <h2 className="font-semibold text-lg text-foreground mb-3">Experiencia</h2>
                  <p className="text-stone-600 leading-relaxed">{listener.experience}</p>
                </div>
              )}

              {/* Reviews */}
              {listener.recent_reviews && listener.recent_reviews.length > 0 && (
                <div className="bg-white rounded-2xl p-6">
                  <h2 className="font-semibold text-lg text-foreground mb-4">Reseñas recientes</h2>
                  <div className="space-y-4">
                    {listener.recent_reviews.map((review) => (
                      <div key={review.id} className="border-b border-stone-100 last:border-0 pb-4 last:pb-0">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} className={`w-4 h-4 ${star <= review.rating ? "text-amber-400 fill-current" : "text-stone-300"}`} />
                            ))}
                          </div>
                          <span className="text-sm text-stone-500">{review.user_name}</span>
                        </div>
                        {review.comment && <p className="text-stone-600">{review.comment}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* FAQ Module - Desktop */}
              <ListenerFaqModule
                listenerName={listener.name}
                onBookClick={openBookingDialog}
                onInstantClick={handleInstantBooking}
                showInstant={listener.is_online && listener.availability_mode === "instant"}
              />

              {/* Trust Block - What you get */}
              <div className="bg-primary/5 rounded-2xl p-6 border border-primary/10">
                <h3 className="font-semibold text-lg text-foreground mb-4">Lo que incluye tu sesión</h3>
                <ul className="space-y-3 text-stone-600">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    50 minutos de conversación privada
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    Videollamada 1 a 1 con {listener.name.split(' ')[0]}
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    100% confidencial
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    Escucha sin juicios
                  </li>
                </ul>
              </div>

              {/* Social Proof */}
              {listener.total_sessions > 0 && (
                <div className="bg-stone-50 rounded-2xl p-6 text-center">
                  <p className="text-stone-600">
                    Más de <strong>{listener.total_sessions}</strong> conversaciones reales con personas que solo necesitaban ser escuchadas.
                  </p>
                </div>
              )}

              {/* Legal Compliance Block */}
              <div className="bg-stone-100 rounded-2xl p-6 text-sm text-stone-500 space-y-2">
                <p><strong>Aviso importante:</strong> {LEGAL_DISCLAIMER}</p>
                <p>Este es un servicio de escucha y conversación, no terapia. {listener.name.split(' ')[0]} no es profesional de salud mental.</p>
              </div>
            </div>

            {/* Right - Booking Sidebar */}
            <div className="col-span-1">
              <div className="bg-white rounded-3xl shadow-lg border border-stone-100 p-6 sticky top-24">
                {/* Pricing Display */}
                <div className="text-center mb-4">
                  {pricingInfo ? (
                    <>
                      <div className="mb-3">
                        <span className="text-4xl font-bold text-primary">
                          ${selectedCurrency === "mxn" ? pricingInfo.price_mxn : pricingInfo.price_usd}
                        </span>
                        <span className="text-stone-500 ml-1 uppercase">{selectedCurrency}</span>
                      </div>
                      <p className="text-sm text-stone-400">
                        {selectedCurrency === "mxn" 
                          ? `≈ $${pricingInfo.price_usd} USD`
                          : `≈ $${pricingInfo.price_mxn} MXN`
                        }
                      </p>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-primary">${listener.price_per_session}</span>
                      <span className="text-stone-500 ml-1">USD / sesión</span>
                    </>
                  )}
                </div>

                {/* Currency Selector */}
                {pricingInfo && (
                  <div className="mb-6">
                    <p className="text-xs text-stone-500 text-center mb-2">
                      {pricingInfo.location?.country_code === "MX" ? "📍 México detectado" : "📍 USA detectado"}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedCurrency("usd")}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          selectedCurrency === "usd"
                            ? "bg-primary text-white"
                            : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                        }`}
                        data-testid="currency-usd-btn"
                      >
                        🇺🇸 USD
                      </button>
                      <button
                        onClick={() => setSelectedCurrency("mxn")}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          selectedCurrency === "mxn"
                            ? "bg-primary text-white"
                            : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                        }`}
                        data-testid="currency-mxn-btn"
                      >
                        🇲🇽 MXN
                      </button>
                    </div>
                  </div>
                )}

                <Button 
                  className="w-full btn-secondary text-lg py-6"
                  onClick={openBookingDialog}
                  data-testid="book-session-btn"
                >
                  <CreditCard className="w-5 h-5 mr-2" />
                  Agendar cita
                </Button>

                <div className="mt-6 space-y-3">
                  {["Sesión de 50 minutos", "Por videollamada", "Acompañamiento personalizado", "Pago seguro con Stripe"].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-stone-600">
                      <CheckCircle className="w-4 h-4 text-accent" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Dialog - Mobile optimized */}
        <Dialog open={showBookingDialog} onOpenChange={setShowBookingDialog}>
          <DialogContent className="max-w-lg w-[95vw] max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader className="pb-2">
              <DialogTitle className="text-xl font-serif">Agendar cita</DialogTitle>
              <DialogDescription className="text-sm">
                Selecciona fecha y horario para tu cita con {listener.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-2">
              {/* Calendar - Centered */}
              <div ref={calendarRef} className="flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => {
                    setSelectedDate(date);
                    setSelectedSlot(null);
                  }}
                  disabled={(date) => date < addDays(new Date(), 0)}
                  locale={es}
                  className="rounded-lg border shadow-sm [&_.rdp-day_selected]:!bg-primary [&_.rdp-day_selected]:!text-white [&_.rdp-day_today]:!bg-stone-100"
                  modifiersClassNames={{
                    selected: "bg-primary text-white hover:bg-primary hover:text-white focus:bg-primary focus:text-white",
                    today: "bg-stone-100 text-foreground"
                  }}
                />
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div ref={slotsRef} className="border-t pt-4">
                  <h3 className="font-medium text-foreground mb-3">
                    Horarios para {format(selectedDate, "d 'de' MMMM", { locale: es })}
                  </h3>
                  
                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      <span className="ml-2 text-stone-500">Cargando horarios...</span>
                    </div>
                  ) : slotsError ? (
                    <div className="flex items-center gap-2 p-4 bg-amber-50 rounded-lg text-amber-700">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <p className="text-sm">{slotsError}</p>
                    </div>
                  ) : availableSlots.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {availableSlots.map((slot, i) => {
                        const slotTime = typeof slot === 'object' ? slot.time : slot;
                        const isSelected = selectedSlot && (typeof selectedSlot === 'object' ? selectedSlot.time : selectedSlot) === slotTime;
                        return (
                          <button
                            key={i}
                            onClick={() => setSelectedSlot(slot)}
                            className={`p-3 rounded-lg text-sm font-medium transition-all ${
                              isSelected
                                ? "bg-primary text-white"
                                : "bg-stone-100 hover:bg-stone-200 text-foreground"
                            }`}
                            data-testid={`slot-${slotTime}`}
                          >
                            {slotTime}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-4 bg-stone-100 rounded-lg text-stone-600">
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      <p className="text-sm">No hay horarios disponibles para este día</p>
                    </div>
                  )}
                </div>
              )}

              {/* Consent */}
              {selectedSlot && (
                <div ref={summaryRef} className="border-t pt-4 space-y-4">
                  {/* Legal disclaimer for USA */}
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-800 leading-relaxed">
                      <strong>Important:</strong> Apoyum Listeners are NOT licensed therapists or mental health professionals. 
                      This service is for emotional support only, not professional therapy. Listeners may be located outside the U.S.
                    </p>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
                    <Checkbox
                      id="consent"
                      checked={consentAccepted}
                      onCheckedChange={setConsentAccepted}
                      className="mt-1"
                    />
                    <label htmlFor="consent" className="text-sm text-stone-600 leading-relaxed cursor-pointer">
                      I understand and accept that this is an emotional support service, NOT professional therapy. 
                      Listeners are NOT licensed mental health professionals and may be located outside the United States.
                    </label>
                  </div>

                  {/* Summary */}
                  <div className="bg-primary/5 rounded-lg p-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-stone-600">Fecha</span>
                      <span className="font-medium">{format(selectedDate, "d 'de' MMMM, yyyy", { locale: es })}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-stone-600">Hora</span>
                      <span className="font-medium">{typeof selectedSlot === 'object' ? selectedSlot.time : selectedSlot}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-primary/10 pt-2 mt-2">
                      <span className="text-stone-600">Total</span>
                      <span className="font-bold text-primary">
                        ${pricingInfo ? (selectedCurrency === "mxn" ? pricingInfo.price_mxn : pricingInfo.price_usd) : listener.price_per_session} {selectedCurrency.toUpperCase()}
                      </span>
                    </div>
                    {pricingInfo && (
                      <p className="text-xs text-stone-400 text-right mt-1">
                        ≈ ${selectedCurrency === "mxn" ? pricingInfo.price_usd + " USD" : pricingInfo.price_mxn + " MXN"}
                      </p>
                    )}
                  </div>

                  {/* Currency selector in dialog */}
                  {pricingInfo && (
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedCurrency("usd")}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          selectedCurrency === "usd"
                            ? "bg-primary text-white"
                            : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                        }`}
                      >
                        🇺🇸 Pagar en USD
                      </button>
                      <button
                        onClick={() => setSelectedCurrency("mxn")}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                          selectedCurrency === "mxn"
                            ? "bg-primary text-white"
                            : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                        }`}
                      >
                        🇲🇽 Pagar en MXN
                      </button>
                    </div>
                  )}

                  <Button
                    className="w-full py-6 text-lg"
                    onClick={handleBooking}
                    disabled={!consentAccepted || booking}
                    data-testid="confirm-booking-btn"
                  >
                    {booking ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Procesando...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Pagar ${pricingInfo ? (selectedCurrency === "mxn" ? pricingInfo.price_mxn : pricingInfo.price_usd) : listener.price_per_session} {selectedCurrency.toUpperCase()}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Instant Booking Dialog */}
        <Dialog open={showInstantDialog} onOpenChange={setShowInstantDialog}>
          <DialogContent className="max-w-md w-[95vw]">
            <DialogHeader>
              <DialogTitle className="text-xl font-serif flex items-center gap-2">
                <Zap className="w-5 h-5 text-green-500" />
                Sesión inmediata
              </DialogTitle>
              <DialogDescription className="text-sm">
                Inicia una sesión inmediata de 50 minutos con {listener.name}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {/* Legal disclaimer */}
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-xs text-amber-800 leading-relaxed">
                  <strong>Important:</strong> Apoyum Listeners are NOT licensed therapists or mental health professionals. 
                  This service is for emotional support only, not professional therapy. Listeners may be located outside the U.S.
                </p>
              </div>

              <div className="flex items-start gap-3 p-3 bg-stone-50 rounded-lg">
                <Checkbox
                  id="instant-consent"
                  checked={instantConsent}
                  onCheckedChange={setInstantConsent}
                  className="mt-1"
                />
                <label htmlFor="instant-consent" className="text-sm text-stone-600 leading-relaxed cursor-pointer">
                  I understand and accept that this is an emotional support service, NOT professional therapy. 
                  Listeners are NOT licensed mental health professionals.
                </label>
              </div>

              {/* Summary */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-green-700 font-medium">Sesión disponible ahora</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-stone-600">Duración</span>
                  <span className="font-medium">50 minutos</span>
                </div>
                <div className="flex justify-between text-sm border-t border-green-200 pt-2 mt-2">
                  <span className="text-stone-600">Total</span>
                  <span className="font-bold text-primary">
                    ${pricingInfo ? (selectedCurrency === "mxn" ? pricingInfo.price_mxn : pricingInfo.price_usd) : listener.price_per_session} {selectedCurrency.toUpperCase()}
                  </span>
                </div>
                {pricingInfo && (
                  <p className="text-xs text-stone-400 text-right mt-1">
                    ≈ ${selectedCurrency === "mxn" ? pricingInfo.price_usd + " USD" : pricingInfo.price_mxn + " MXN"}
                  </p>
                )}
              </div>

              {/* Currency selector */}
              {pricingInfo && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedCurrency("usd")}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      selectedCurrency === "usd"
                        ? "bg-primary text-white"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    🇺🇸 Pagar en USD
                  </button>
                  <button
                    onClick={() => setSelectedCurrency("mxn")}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                      selectedCurrency === "mxn"
                        ? "bg-primary text-white"
                        : "bg-stone-100 text-stone-600 hover:bg-stone-200"
                    }`}
                  >
                    🇲🇽 Pagar en MXN
                  </button>
                </div>
              )}

              <Button
                className="w-full py-6 text-lg bg-green-500 hover:bg-green-600"
                onClick={confirmInstantBooking}
                disabled={!instantConsent || booking}
                data-testid="confirm-instant-btn"
              >
                {booking ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Zap className="w-5 h-5 mr-2" />
                    Pagar ${pricingInfo ? (selectedCurrency === "mxn" ? pricingInfo.price_mxn : pricingInfo.price_usd) : listener.price_per_session} {selectedCurrency.toUpperCase()}
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      
      {/* Chatbot Widget - Siempre visible en perfiles de listeners */}
      <ChatbotWidget 
        listenerId={listener?.id}
        listenerName={listener?.name}
        onAgendarClick={openBookingDialog}
      />
    </Layout>
  );
}
